'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import type { SearchResponse } from '@algolia/client-search';

import { useAppDispatch, useAppStore } from '@/modules/common/hooks/useStore';
import { useUiKey } from '@/modules/common/hooks/useUiKey';
import { urlSearchParamsToQueryString } from '@/modules/common/lib/utils';
import {
  ALGOLIA_ACCORDION_FILTERS_MAX_ITEMS,
  ALGOLIA_COLLECTION_PUBLISHED_FILTER,
} from '@/modules/common/services/algoliaConstants';
import { ALGOLIA_COLLECTION_INDEX } from '@/modules/common/services/constants';
import { useFinder } from '@/modules/cora/context/FinderProvider';
import { searchClient } from '@/modules/product/components/AlgoliaSearch/AlgoliaProvider';
import { ROOMTYPE_FACET } from '@/modules/product/components/collections/constants';
import { useCategoryContext } from '@/modules/product/context/CategoryContext';
import {
  ALGOLIA_SEARCH_INDEX,
  LISTING_COLLECTIONS_HITS_PER_PAGE,
  LISTING_HITS_PER_PAGE,
} from '@/modules/product/services/consts';
import {
  buildAlgoliaFilters,
  getRefinedAttributes,
  mergeDisjunctiveFacets,
  parseFiltersFromUrl,
  stripAttributeFromFacetFilters,
  stripAttributeFromNumericFilters,
} from '@/modules/product/services/routingHelpers';
import {
  type SimplifiedCollectionResults,
  type SimplifiedSamplesResults,
  type SimplifiedSerpPayload,
  getSimplifiedSerpFromState,
  syncSimplifiedSearchPage,
} from '@/modules/product/state/searchResultsSlice';

type SearchPageResults = {
  samplesResults: SimplifiedSamplesResults | null;
  collectionResults: SimplifiedCollectionResults | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  isLastPage: boolean;
  loadMore: () => void;
  facetsForFilters: Record<string, Record<string, number>>;
  renderingContentForFilters: SearchResponse['renderingContent'] | undefined;
};

type SamplesResults = SearchPageResults['samplesResults'];
type CollectionResults = SearchPageResults['collectionResults'];

function urlPageFromAlgoliaLastPage(loadedMaxAlgoliaPage: number): number {
  return loadedMaxAlgoliaPage + 1;
}

function locationPathFromUrl(url: URL) {
  const qs = urlSearchParamsToQueryString(url.searchParams);
  return `${url.pathname}${qs ? `?${qs}` : ''}${url.hash}`;
}

function stripPageSearchParam() {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  if (!url.searchParams.has('page')) {
    return;
  }
  url.searchParams.delete('page');
  window.history.replaceState(window.history.state, '', locationPathFromUrl(url));
}

export function useSearchPageQuery(): SearchPageResults {
  const searchParams = useSearchParams();
  const { filtersDefinition } = useFinder();
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const uiKey = useUiKey();
  useCategoryContext();

  const queryParams = useMemo(() => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [searchParams]);

  const routeState = useMemo(() => {
    return parseFiltersFromUrl({
      queryParams,
      filtersDefinition,
      separator: '|',
    });
  }, [queryParams, filtersDefinition]);

  const query = queryParams.query || '';

  const { facetFilters, numericFilters } = useMemo(
    () => buildAlgoliaFilters(routeState, filtersDefinition),
    [routeState, filtersDefinition]
  );

  const facetAttributes = useMemo(() => {
    return filtersDefinition.map((f) => f.attribute).filter((attr): attr is string => !!attr);
  }, [filtersDefinition]);

  const roomtypeRefinements = routeState.refinements[ROOMTYPE_FACET];
  const applicationFilter = Array.isArray(roomtypeRefinements) ? roomtypeRefinements[0] : undefined;
  const collectionsFilter =
    applicationFilter ?
      `${ROOMTYPE_FACET}:'${applicationFilter}' AND ${ALGOLIA_COLLECTION_PUBLISHED_FILTER}`
    : ALGOLIA_COLLECTION_PUBLISHED_FILTER;

  const queryKey = useMemo(() => {
    return JSON.stringify({
      query,
      facetFilters: facetFilters ? [...facetFilters].sort() : undefined,
      numericFilters: numericFilters ? [...numericFilters].sort() : undefined,
      facetAttributes: [...facetAttributes].sort(),
      collectionsFilter,
    });
  }, [query, facetFilters, numericFilters, facetAttributes, collectionsFilter]);

  const initialSnapshot = useRef(getSimplifiedSerpFromState(store.getState(), uiKey)).current;

  const initialFacetsForRef = initialSnapshot?.samplesResults?.facets;
  const lastFacetsRef = useRef<Record<string, Record<string, number>>>(
    initialFacetsForRef && Object.keys(initialFacetsForRef).length > 0 ? initialFacetsForRef : {}
  );
  const lastRenderingContentRef = useRef<SearchResponse['renderingContent'] | undefined>(
    initialSnapshot?.samplesResults?.renderingContent
  );

  const [samplesResults, setSamplesResults] = useState<SamplesResults>(
    () => initialSnapshot?.samplesResults ?? null
  );
  const [collectionResults, setCollectionResults] = useState<CollectionResults>(
    () => initialSnapshot?.collectionResults ?? null
  );
  const [isLoading, setIsLoading] = useState(() => !initialSnapshot?.samplesResults);
  const [loadedMaxPage, setLoadedMaxPage] = useState(() => initialSnapshot?.loadedMaxPage ?? -1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const loadMoreInFlightRef = useRef(false);
  const loadedMaxPageRef = useRef(initialSnapshot?.loadedMaxPage ?? -1);
  const nbPagesRef = useRef(initialSnapshot?.samplesResults?.nbPages ?? 0);
  const collectionResultsRef = useRef<CollectionResults>(
    initialSnapshot?.collectionResults ?? null
  );
  const uiKeyRef = useRef(uiKey);
  uiKeyRef.current = uiKey;

  useEffect(() => {
    collectionResultsRef.current = collectionResults;
  }, [collectionResults]);

  useEffect(() => {
    loadedMaxPageRef.current = loadedMaxPage;
  }, [loadedMaxPage]);

  useEffect(() => {
    nbPagesRef.current = samplesResults?.nbPages ?? 0;
  }, [samplesResults?.nbPages]);

  // While a new search runs, `samplesResults` is briefly null - keep previous facets for the filter UI
  const nextFacets = samplesResults?.facets;
  if (nextFacets && Object.keys(nextFacets).length > 0) {
    lastFacetsRef.current = nextFacets;
  }
  const facetsForFilters =
    nextFacets && Object.keys(nextFacets).length > 0 ? nextFacets : lastFacetsRef.current;

  const nextRendering = samplesResults?.renderingContent;
  if (nextRendering) {
    lastRenderingContentRef.current = nextRendering;
  }
  const renderingContentForFilters = nextRendering ?? lastRenderingContentRef.current;

  const buildSamplesParams = useCallback(
    (pageIndex: number, analytics: boolean): Record<string, unknown> => ({
      query: query.trim(),
      facets: facetAttributes,
      ...(facetFilters ? { facetFilters } : {}),
      ...(numericFilters ? { numericFilters } : {}),
      hitsPerPage: LISTING_HITS_PER_PAGE,
      maxValuesPerFacet: ALGOLIA_ACCORDION_FILTERS_MAX_ITEMS,
      page: pageIndex,
      clickAnalytics: true,
      analytics,
      getRankingInfo: true,
    }),
    [query, facetAttributes, facetFilters, numericFilters]
  );

  const buildCollectionParams = useCallback(
    (): Record<string, unknown> => ({
      query: query.trim(),
      filters: collectionsFilter,
      hitsPerPage: LISTING_COLLECTIONS_HITS_PER_PAGE,
    }),
    [query, collectionsFilter]
  );

  /**
   * Disjunctive faceting (manual): for every refined attribute, compute facet
   * counts as if that attribute's own filters were absent. Otherwise the
   * sidebar shows only the user's selected value (greyed out / impossible to
   * pick a second value), which is precisely the behaviour
   * `algoliasearch-helper` hides for the InstantSearch-based architecture.
   */
  const refinedAttributes = useMemo(
    () => getRefinedAttributes(routeState, filtersDefinition),
    [routeState, filtersDefinition]
  );

  const buildDisjunctiveAuxRequests = useCallback(() => {
    return refinedAttributes.map((attribute) => {
      const auxFacetFilters = stripAttributeFromFacetFilters(facetFilters, attribute);
      const auxNumericFilters = stripAttributeFromNumericFilters(numericFilters, attribute);
      return {
        indexName: ALGOLIA_SEARCH_INDEX,
        params: {
          query: query.trim(),
          facets: [attribute],
          ...(auxFacetFilters ? { facetFilters: auxFacetFilters } : {}),
          ...(auxNumericFilters ? { numericFilters: auxNumericFilters } : {}),
          hitsPerPage: 0,
          maxValuesPerFacet: ALGOLIA_ACCORDION_FILTERS_MAX_ITEMS,
          analytics: false,
        },
      };
    });
  }, [refinedAttributes, facetFilters, numericFilters, query]);

  const persistToRedux = useCallback(
    (
      samples: SamplesResults,
      collections: CollectionResults,
      maxAlgoliaPage: number,
      isLast: boolean
    ) => {
      if (!samples) {
        return;
      }
      const payload: SimplifiedSerpPayload = {
        samplesResults: samples,
        collectionResults: collections,
        loadedMaxPage: maxAlgoliaPage,
      };
      dispatch(
        syncSimplifiedSearchPage({
          uiKey: uiKeyRef.current,
          simplified: payload,
          isLastPage: isLast,
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    let cancelled = false;

    const fromRedux = getSimplifiedSerpFromState(store.getState(), uiKey);
    if (fromRedux?.samplesResults?.hits?.length) {
      setSamplesResults(fromRedux.samplesResults);
      setCollectionResults(fromRedux.collectionResults);
      setLoadedMaxPage(fromRedux.loadedMaxPage);
      loadedMaxPageRef.current = fromRedux.loadedMaxPage;
      nbPagesRef.current = fromRedux.samplesResults?.nbPages ?? 0;
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setSamplesResults(null);
    setCollectionResults(null);
    setLoadedMaxPage(-1);
    loadedMaxPageRef.current = -1;

    stripPageSearchParam();

    async function fetchInitialPagesAndShelf() {
      setIsLoading(true);
      setError(null);

      try {
        const auxRequests = buildDisjunctiveAuxRequests();
        const results = await searchClient.search([
          {
            indexName: ALGOLIA_SEARCH_INDEX,
            params: buildSamplesParams(0, true),
          },
          {
            indexName: ALGOLIA_COLLECTION_INDEX,
            params: buildCollectionParams(),
          },
          ...auxRequests,
        ]);

        if (cancelled) {
          return;
        }

        const samplesResult = results.results[0] as SearchResponse<unknown>;
        const pageQID = samplesResult.queryID;
        const hits = (samplesResult.hits || []).map((hit) =>
          pageQID ? { ...(hit as object), __queryID: pageQID } : hit
        );

        const nbPages = samplesResult.nbPages || 0;
        nbPagesRef.current = nbPages;

        const auxResults = results.results.slice(2) as SearchResponse<unknown>[];
        const auxFacetCounts = auxRequests.map((req, idx) => ({
          attribute: refinedAttributes[idx],
          facetCounts: (auxResults[idx]?.facets as Record<string, Record<string, number>>)?.[
            refinedAttributes[idx]
          ],
        }));
        const mergedFacets = mergeDisjunctiveFacets(
          (samplesResult.facets as Record<string, Record<string, number>>) || {},
          auxFacetCounts
        );

        const newSamplesResults: SamplesResults = {
          hits,
          facets: mergedFacets,
          renderingContent: samplesResult.renderingContent,
          nbHits: samplesResult.nbHits || 0,
          page: 0,
          nbPages,
          query: samplesResult.query || '',
          queryID: samplesResult.queryID,
          abTestID: (samplesResult as SearchResponse<unknown> & { abTestID?: number }).abTestID,
          abTestVariantID: (samplesResult as SearchResponse<unknown> & { abTestVariantID?: number })
            .abTestVariantID,
        };

        setSamplesResults(newSamplesResults);

        const actualMaxPage = 0;
        setLoadedMaxPage(actualMaxPage);
        loadedMaxPageRef.current = actualMaxPage;

        const collectionResult = results.results[1] as SearchResponse<unknown> | undefined;
        const newCollectionResults: CollectionResults =
          collectionResult ?
            {
              hits: collectionResult.hits || [],
              nbHits: collectionResult.nbHits || 0,
            }
          : null;
        setCollectionResults(newCollectionResults);

        const last = nbPages <= 0 || actualMaxPage >= nbPages - 1;
        persistToRedux(newSamplesResults, newCollectionResults, actualMaxPage, last);
      } catch (err) {
        if (cancelled) {
          return;
        }
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setSamplesResults(null);
        setCollectionResults(null);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchInitialPagesAndShelf();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, uiKey]);

  useEffect(() => {
    if (loadedMaxPage < 0) {
      return;
    }
    const url = new URL(window.location.href);
    const urlPage = urlPageFromAlgoliaLastPage(loadedMaxPage);
    if (urlPage > 1) {
      url.searchParams.set('page', String(urlPage));
    } else {
      url.searchParams.delete('page');
    }
    window.history.replaceState(window.history.state, '', locationPathFromUrl(url));
  }, [loadedMaxPage]);

  const loadMore = useCallback(async () => {
    if (isLoading || isLoadingMore || loadMoreInFlightRef.current) {
      return;
    }

    const nbPages = nbPagesRef.current;
    const lmp = loadedMaxPageRef.current;
    if (nbPages <= 0 || lmp >= nbPages - 1) {
      return;
    }

    const nextPage = lmp + 1;
    loadMoreInFlightRef.current = true;
    setIsLoadingMore(true);

    try {
      const results = await searchClient.search([
        {
          indexName: ALGOLIA_SEARCH_INDEX,
          params: buildSamplesParams(nextPage, true),
        },
      ]);

      const samplesResult = results.results[0] as SearchResponse<unknown>;
      const pageQueryID = samplesResult.queryID;
      const newHits = (samplesResult.hits || []).map((hit) =>
        pageQueryID ? { ...(hit as object), __queryID: pageQueryID } : hit
      );

      let updated: SamplesResults = null;
      setSamplesResults((prev) => {
        if (!prev) {
          return prev;
        }
        updated = {
          ...prev,
          hits: [...prev.hits, ...newHits],
          page: samplesResult.page ?? nextPage,
        };
        return updated;
      });

      setLoadedMaxPage(nextPage);
      loadedMaxPageRef.current = nextPage;

      if (updated) {
        const isLast = nbPages <= 0 || nextPage >= nbPages - 1;
        persistToRedux(updated, collectionResultsRef.current, nextPage, isLast);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      loadMoreInFlightRef.current = false;
      setIsLoadingMore(false);
    }
  }, [isLoading, isLoadingMore, buildSamplesParams, persistToRedux]);

  const isLastPage =
    !samplesResults || samplesResults.nbPages <= 0 || loadedMaxPage >= samplesResults.nbPages - 1;

  return {
    samplesResults,
    collectionResults,
    isLoading,
    isLoadingMore,
    error,
    isLastPage,
    loadMore,
    facetsForFilters,
    renderingContentForFilters,
  };
}
